import styled from 'styled-components'
import { Body } from '@shipt/design-system-typography'
import { Row, Stack } from '@shipt/design-system-layouts'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { keyframes } from '@/features/shared/styles/effects'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import {
  type RetailerSelectionDrawerData,
  type PlaceholderData,
} from '@/features/cms/components/CMS/types'
import { useIsEligibleForC360NoMarkup } from '@/features/entrypoint/components/Circle360/useIsEligibleForC360NoMarkup'
import { DynamicRetailerSelectionDrawer } from '@/features/cms/components/CMS/components/RetailerSelectionDrawer/DynamicRetailerSelectionDrawer'
import { C360Logo } from '@/features/shared/components/C360Logo'
import { useQueryCMSContentAlias } from '@/features/cms/services/CMS/queries'
import { TextButton } from '@shipt/design-system-buttons'
import { ChevronRightIcon } from '@shipt/design-system-icons'
import { useEffect } from 'react'
import { trackElementClicked, trackElementViewed } from '@/features/shared/analytics/element'
import { logInfo } from '@/features/shared/utils/logger'
import { noop } from '@/features/shared/utils/noop'
import { theme } from '@/features/shared/constants/shiptTheme'

export const Circle360EvergreenNMUBanner = ({
  content_type_id,
  id,
}: Pick<PlaceholderData, 'content_type_id' | 'id'>) => {
  const isEligibleForC360NoMarkup = useIsEligibleForC360NoMarkup()
  const { data, isFetched, isError } = useQueryCMSContentAlias(
    '360-nmu-retailer-selection-popover',
    { enabled: isEligibleForC360NoMarkup },
  )
  const drawerInnerData = data?.data
  const hasStores = Boolean(drawerInnerData?.retailer_cards?.length)

  const isFetchedWithoutCorrectData = isFetched && (!data?.data || !hasStores || isError)
  const shouldHideBanner = !isEligibleForC360NoMarkup || isFetchedWithoutCorrectData

  useEffect(() => {
    if (isFetched) {
      if (!data?.data) {
        logInfo(
          'No data returned on Circle360EvergreenNMUBanner from 360-nmu-retailer-selection-popover CMS alias',
        )
      } else if (!hasStores) {
        logInfo(
          'No retailer_cards returned on Circle360EvergreenNMUBanner from 360-nmu-retailer-selection-popover CMS alias',
        )
      }
    }
  }, [data?.data, hasStores, isFetched])

  if (data && !shouldHideBanner) {
    return (
      <Circle360EvergreenNMUBannerContent data={data} content_type_id={content_type_id} id={id} />
    )
  }
  return (
    <CMSWrapper {...getCMSWrapperProps({ content_type_id, id })} aria-hidden>
      <BannerContent onClick={noop} />
    </CMSWrapper>
  )
}

const Circle360EvergreenNMUBannerContent = ({
  content_type_id,
  id,
  data,
}: Pick<PlaceholderData, 'content_type_id' | 'id'> & {
  data: RetailerSelectionDrawerData
}) => {
  const { openDialog } = useHandleDialog()

  useEffect(() => {
    trackElementViewed({
      display_sublocation: 'evergreen_ghp_nmu_experience',
      link_name: 'Circle 360 no markups at all stores',
      content: 'Shop with no markups',
      type: 'banner',
    })
  }, [])

  const handleBannerClick = () => {
    openDialog(DynamicRetailerSelectionDrawer, data)
    trackElementClicked({
      next_location: 'retailer_selection_drawer',
      display_sublocation: 'evergreen_ghp_nmu_experience',
      link_name: 'Circle 360 no markups at all stores',
      content: 'Shop with no markups',
      type: 'banner',
    })
  }

  return (
    <CMSWrapper {...getCMSWrapperProps({ content_type_id, id })}>
      <BannerContent onClick={handleBannerClick} />
    </CMSWrapper>
  )
}

const BannerContent = ({ onClick }: { onClick: () => void }) => {
  return (
    <Row
      padding={{ base: ['lg', 0], md: ['lg', 0] }}
      spacing={{ base: 'md', md: 'lg' }}
      align="center"
      justify={{ base: 'start', md: 'center' }}
      className="fullWidthAtTablet"
    >
      <C360Logo />
      <Stack direction={{ base: 'column', md: 'row' }} spacing={{ base: 'xs', md: 'lg' }} wrap>
        <Stack spacing={{ base: 'xs', md: 'sm' }} direction={{ base: 'column', md: 'row' }} wrap>
          <Body size={{ base: 'lg', md: 'xl' }} lineHeight="compact">
            No markups at stores near you.
          </Body>
          <Body size={{ base: 'sm', md: 'xl' }} lineHeight="compact" variant="secondary">
            Excludes select alcohol retailers and items*
          </Body>
        </Stack>
        <TextButton
          trailingIcon={ChevronRightIcon}
          size={{ base: 'md', md: 'lg' }}
          onClick={onClick}
        >
          Learn more
        </TextButton>
      </Stack>
    </Row>
  )
}

const CMSWrapper = styled(GridLayoutContainer)`
  &[aria-hidden='true'] {
    visibility: hidden;
  }

  --enter-y: -50px;
  animation: ${keyframes.enter} 0.6s ease-out 0.2s forwards;
  background: ${theme.color.background.base.neutral.subtle_00};
`
